/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { EMPTY, from, NEVER } from 'rxjs';
import { RootState } from '../store.typings';
import {
    fetchCashRegistersSuccessAction,
    fetchCitiesListSuccessActon,
    fetchFirmwareVersionsListSuccessAction,
    fetchFiscalDriverVersionListSuccessAction,
    fetchLowerActivitiesListSuccessActon,
    fetchMainActivitiesListSuccessActon,
    fetchMiddleActivitiesListSuccessActon,
    fetchOperatorsListSuccessAction,
    fetchRegionsListSuccessActon,
    fetchStoreTypesSuccessActon,
    fetchTaxationTypesListSuccessAction,
    fetchTaxAuthoritiesListSuccessAction,
    fetchUsersListSuccessAction,
    ShortListsAction,
} from '../actions/AShortLists';
import { CashBoxesAction, CashBoxesActionType } from '../actions/CashBoxRegistry/cashBoxesActions';
import { StoresMapAction, StoresMapActionType } from '../actions/AStoresMap';
import { CompaniesAction, CompaniesActionType } from '../actions/companiesActions';
import { StoresActions, StoresActionType } from '../actions/storesActions';
import { ReportsActions, ReportsActionType } from '../actions/reportsActions';
import { CashRegisterModelsAction, CashRegisterModelsActionType } from '../actions/cashRegisterModelsActions';
import { AlertsAction, AlertsActionType } from '../actions/alertsActions';
import { AlertsSettingsActionType } from '../actions/alertsSettingsActions';
import { AlertsDetailsActionType } from '../actions/alertDetailsActions';
import { AdminUsersActionType } from '../actions/adminUsersActions';
import { DevicesActionType } from '../actions/devicesActions';
import { shortListsClient } from '../api/shortLists/shortListsClient';
import { ServiceActionType } from '../actions/serviceActions';
import { CashRegistersListActionType } from '../actions/cashRegistersActions';
import { canAccess } from '../../accessibility/ability';

type ShortListsEpic = Epic<
    | ShortListsAction
    | CashBoxesAction
    | StoresMapAction
    | CompaniesAction
    | StoresActions
    | ReportsActions
    | CashRegisterModelsAction
    | AlertsAction,
    | ShortListsAction
    | CashBoxesAction
    | StoresMapAction
    | CompaniesAction
    | StoresActions
    | ReportsActions
    | CashRegisterModelsAction
    | AlertsAction,
    RootState
>;

export const fetchStoreTypesEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
            StoresMapActionType.OPEN_STORES_MAP_PAGE,
            ReportsActionType.OPEN_REPORTS_PAGE,
        ),

        switchMap(() => {
            const { storeTypesList } = state.value.shortListsReducer;
            //for the moment we use the same short list for store types and company activities
            if ((!storeTypesList || storeTypesList.length === 0) && canAccess(state.value, 'STORE_ACTIVITY')) {
                return from(shortListsClient.getStoreTypes()).pipe(
                    map((storeTypes) => fetchStoreTypesSuccessActon(storeTypes)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                                StoresMapActionType.EXIT_STORES_MAP_PAGE,
                                ReportsActionType.EXIT_REPORTS_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchCompanyActivitiesEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE),

        switchMap(() => {
            //we use the same short list for store types and company activities
            const { storeTypesList } = state.value.shortListsReducer;
            if ((!storeTypesList || storeTypesList.length === 0) && canAccess(state.value, 'RELATED_COMPANY_DATA')) {
                return from(shortListsClient.getStoreTypes()).pipe(
                    map((storeTypes) => fetchStoreTypesSuccessActon(storeTypes)),

                    catchError(() => NEVER),

                    takeUntil(action$.pipe(ofType(CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE))),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchCashRegisterModelsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, ServiceActionType.OPEN_SERVICE_PAGE),
        switchMap(() => {
            const { cashRegisterModelsList } = state.value.shortListsReducer;
            if (
                (!cashRegisterModelsList || cashRegisterModelsList.length === 0) &&
                canAccess(state.value, 'CASHREGISTER_MODELS_FILTER')
            ) {
                return from(shortListsClient.getCashRegisterModels()).pipe(
                    map((list) => fetchCashRegistersSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                ServiceActionType.EXIT_SERVICE_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchOperatorsShortListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CashRegisterModelsActionType.OPEN_REGISTRY_CASH_REGISTER_MODELS_LIST_PAGE,
            CashRegistersListActionType.OPEN_REGISTRY_CASH_REGISTERS_LIST_PAGE,
            DevicesActionType.OPEN_REGISTRY_DEVICES_LIST_PAGE,
            AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE,
            ServiceActionType.OPEN_SERVICE_PAGE,
        ),
        switchMap(() => {
            const { operatorsShortList } = state.value.shortListsReducer;
            if (
                (!operatorsShortList || operatorsShortList.length === 0) &&
                canAccess(state.value, 'RELATED_OPERATOR_DATA')
            ) {
                return from(shortListsClient.getOperatorsList()).pipe(
                    map((list) => fetchOperatorsListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                CashRegisterModelsActionType.EXIT_REGISTRY_CASH_REGISTER_MODELS_LIST_PAGE,
                                CashRegistersListActionType.EXIT_REGISTRY_CASH_REGISTERS_LIST_PAGE,
                                DevicesActionType.EXIT_REGISTRY_DEVICES_LIST_PAGE,
                                AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE,
                                ServiceActionType.EXIT_SERVICE_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchFirmwareVersionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            DevicesActionType.OPEN_REGISTRY_DEVICES_LIST_PAGE,
            ServiceActionType.OPEN_SERVICE_PAGE,
        ),
        switchMap(() => {
            const { firmwareVersionsList } = state.value.shortListsReducer;
            if (
                (!firmwareVersionsList || firmwareVersionsList.length === 0) &&
                canAccess(state.value, 'CASHBOX_HARDWARE_AND_SOFTWARE_DATA')
            ) {
                return from(shortListsClient.getFirmwareVersions()).pipe(
                    map((list) => fetchFirmwareVersionsListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                DevicesActionType.EXIT_REGISTRY_DEVICES_LIST_PAGE,
                                ServiceActionType.EXIT_SERVICE_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchFiscalDriverVersionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, ServiceActionType.OPEN_SERVICE_PAGE),
        switchMap(() => {
            const { fiscalDriverVersionsList } = state.value.shortListsReducer;
            if (
                (!fiscalDriverVersionsList || fiscalDriverVersionsList.length === 0) &&
                canAccess(state.value, 'CASHBOX_HARDWARE_AND_SOFTWARE_DATA')
            ) {
                return from(shortListsClient.getFiscalDriverVersions()).pipe(
                    map((list) => fetchFiscalDriverVersionListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                ServiceActionType.EXIT_SERVICE_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchTaxAuthoritiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
            AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE,
        ),
        switchMap(() => {
            const { taxAuthorityList } = state.value.shortListsReducer;

            if ((!taxAuthorityList || taxAuthorityList.length === 0) && canAccess(state.value, 'TAX_AUTHORITY')) {
                return from(shortListsClient.getTaxAuthorities()).pipe(
                    map((list) => fetchTaxAuthoritiesListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                                AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchTaxationTypesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
        ),
        switchMap(() => {
            const { taxationTypesList } = state.value.shortListsReducer;
            if (
                (!taxationTypesList || taxationTypesList.length === 0) &&
                canAccess(state.value, 'RELATED_COMPANY_DATA')
            ) {
                return from(shortListsClient.getTaxationTypes()).pipe(
                    map((list) => fetchTaxationTypesListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchCitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(StoresMapActionType.OPEN_STORES_MAP_PAGE),
        switchMap(() => {
            const { cities } = state.value.shortListsReducer;
            //no access permits
            if (!cities || cities.length === 0) {
                return from(shortListsClient.getCities()).pipe(
                    map((cities) => fetchCitiesListSuccessActon(cities)),

                    catchError(() => NEVER),

                    takeUntil(action$.pipe(ofType(StoresMapActionType.EXIT_STORES_MAP_PAGE))),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchRegionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { regions } = state.value.shortListsReducer;
            //no access permits
            if (!regions || regions.length === 0) {
                return from(shortListsClient.getRegions()).pipe(
                    map((list) => fetchRegionsListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchAlertAssigneeUsersListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            AlertsActionType.OPEN_ALERTS_PAGE,
            AlertsSettingsActionType.OPEN_ALERTS_SETTINGS_PAGE,
            AlertsDetailsActionType.OPEN_ALERTS_DETAILS_PAGE,
        ),
        switchMap(() => {
            const { alertAssigneeUsersList } = state.value.shortListsReducer;

            if (
                (!alertAssigneeUsersList || alertAssigneeUsersList.length === 0) &&
                canAccess(state.value, 'ALERT_ASSIGNEE_USERS_LIST')
            ) {
                return from(shortListsClient.getUsersList()).pipe(
                    map((users) => fetchUsersListSuccessAction(users)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                AlertsActionType.EXIT_ALERTS_PAGE,
                                AlertsSettingsActionType.EXIT_ALERTS_SETTINGS_PAGE,
                                AlertsDetailsActionType.EXIT_ALERTS_DETAILS_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchMainActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { mainActivitiesList } = state.value.shortListsReducer;

            if ((!mainActivitiesList || mainActivitiesList.length === 0) && canAccess(state.value, 'STORE_ACTIVITY')) {
                return from(shortListsClient.getListOfMainActivities()).pipe(
                    map((list) => fetchMainActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchMiddleActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { middleActivitiesList } = state.value.shortListsReducer;

            if (
                (!middleActivitiesList || middleActivitiesList.length === 0) &&
                canAccess(state.value, 'STORE_ACTIVITY')
            ) {
                return from(shortListsClient.getListOfMiddleActivities()).pipe(
                    map((list) => fetchMiddleActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchLowerActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { lowerActivitiesList } = state.value.shortListsReducer;

            if (
                (!lowerActivitiesList || lowerActivitiesList.length === 0) &&
                canAccess(state.value, 'STORE_ACTIVITY')
            ) {
                return from(shortListsClient.getListOfLowerActivities()).pipe(
                    map((list) => fetchLowerActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.pipe(
                            ofType(
                                CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                                StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ),
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};
